import { safeFetch, getBaseUrl, authFetch } from './api';

// All routes: /patients/:patientId/documents/...
const url = (path: string) => `${getBaseUrl()}${path}`;

export type DocumentType = 'external_prescription' | 'clinical_photo' | 'identity_document' | 'other';

export interface PatientDocument {
  id: string;
  patient_id: string;
  prescription_id: string | null;
  admission_id: string | null;
  document_type: DocumentType;
  body_site: string | null;
  document_date: string;
  photo_consent_confirmed: number;
  file_name: string;
  mime_type: string;
  file_size: number;
  uploaded_by: string;
  created_at: string;
}

export interface DocumentLink {
  prescriptionId?: string;
  admissionId?: string;
}

export interface UploadPatientDocumentsPayload extends DocumentLink {
  documentType: DocumentType;
  bodySite?: string;
  documentDate: string; // 'YYYY-MM-DD'
  photoConsentConfirmed?: boolean;
  files: File[];
}

export interface UploadPatientDocumentsResult {
  created: PatientDocument[];
  errors: { file_name: string; message: string }[];
}

function linkParams(link: DocumentLink): string {
  const params = new URLSearchParams();
  if (link.prescriptionId) params.set('prescription_id', link.prescriptionId);
  if (link.admissionId) params.set('admission_id', link.admissionId);
  return params.toString();
}

export async function listPatientDocuments(patientId: string, link: DocumentLink): Promise<PatientDocument[]> {
  return safeFetch<PatientDocument[]>(url(`/patients/${patientId}/documents?${linkParams(link)}`));
}

// Upload uses FormData directly (not safeFetch) — safeFetch always sends
// application/json and would break the multipart boundary if set manually.
export async function uploadPatientDocuments(
  patientId: string,
  payload: UploadPatientDocumentsPayload
): Promise<UploadPatientDocumentsResult> {
  const formData = new FormData();
  formData.append('document_type', payload.documentType);
  if (payload.prescriptionId) formData.append('prescription_id', payload.prescriptionId);
  if (payload.admissionId) formData.append('admission_id', payload.admissionId);
  if (payload.bodySite) formData.append('body_site', payload.bodySite);
  formData.append('document_date', payload.documentDate);
  formData.append('photo_consent_confirmed', String(!!payload.photoConsentConfirmed));
  for (const file of payload.files) formData.append('files', file);

  const res = await authFetch(url(`/patients/${patientId}/documents`), {
    method: 'POST',
    body: formData,
  });

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error(data?.error || `Upload failed (HTTP ${res.status})`);
  }
  return data as UploadPatientDocumentsResult;
}

export async function deletePatientDocument(patientId: string, documentId: string): Promise<{ success: boolean }> {
  return safeFetch(url(`/patients/${patientId}/documents/${documentId}`), { method: 'DELETE' });
}

// The streaming endpoint requires a Bearer token, which a plain <a href>/
// <img src> can't send — so callers fetch the bytes and get back an object
// URL. Caller owns the returned URL and must URL.revokeObjectURL it when done.
export async function fetchPatientDocumentBlobUrl(patientId: string, documentId: string): Promise<string> {
  const res = await authFetch(url(`/patients/${patientId}/documents/${documentId}`));
  if (!res.ok) {
    throw new Error(`Failed to load document (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Opens a document inline in a new tab (used for the click-to-enlarge action).
export async function openPatientDocumentInline(patientId: string, documentId: string): Promise<void> {
  const objectUrl = await fetchPatientDocumentBlobUrl(patientId, documentId);
  window.open(objectUrl, '_blank');
  // Revoke after giving the new tab time to load the resource.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
